import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';

import { Channels } from './channels';
import { SelectedOpenings } from './selected-openings';

export function SelectChannelsDialog() {
  const { postJobPath } = useNavigationPaths();
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);
  const {
    openChannelsDialog,
    setOpenChannelsDialog,
    selectedChannelSlugs,
    setSelectedOpeningIds,
    selectedOpeningIds,
    unsupportedOpenings,
    clearJobPostingStorage,
  } = useJobPostingContext();

  const handleNextClick = () => {
    // Remove openings that are unsupported by ALL selected channels
    setSelectedOpeningIds(prev =>
      prev.filter(openingId => {
        const isUnsupportedEverywhere = selectedChannelSlugs.every(slug =>
          (unsupportedOpenings[slug] ?? []).includes(openingId),
        );
        return !isUnsupportedEverywhere;
      }),
    );

    // Mark that we're navigating (not dismissing)
    isNavigatingRef.current = true;
    setOpenChannelsDialog(false);
    navigate(postJobPath);
  };

  const handleOpenChange = (open: boolean) => {
    setOpenChannelsDialog(open);

    // Clear storage when dialog is dismissed (user clicked X, Escape, or outside) (not when navigating via Next button)
    if (!open && !isNavigatingRef.current) clearJobPostingStorage();

    isNavigatingRef.current = false;
  };

  return (
    <Dialog open={openChannelsDialog} onOpenChange={handleOpenChange}>
      <DialogContent className="min-w-fit">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="{count, plural, one {Post opening to job boards} other {Post # openings to job boards}}"
              description="Title of the dialog to post to job boards"
              values={{ count: selectedOpeningIds.length }}
            />
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-6 p-4">
          <SelectedOpenings />
          <Channels />
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            disabled={selectedChannelSlugs.length === 0}
            onClick={handleNextClick}
          >
            <FormattedMessage
              defaultMessage="Next"
              description="Button to navigate to the next page"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
